import NumberDisplay from '../NumberDisplay/NumberDisplay';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import {
	BigNum,
	BASE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { UIMarket, UIOrderType } from '@drift/common';
import { AccountOpenPosition } from 'src/@types/positions';
import useClosePositionWithMarketOrder from 'src/hooks/positions/useClosePositionWithMarketOrder';
import MobileTableCard from '../Tables/cards/MobileTableCard';
import { useState } from 'react';
import MarketCellV2 from '../Tables/cells/MarketCellV2';
import { cj } from 'src/utils/classNames';
import { TakeProfitStopLossButton } from 'src/components/Buttons/TakeProfitStopLossButton';
import { Health, Share, Swap } from '@drift-labs/icons';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import IconButton from '../primitives/IconButton/IconButton';
import Button from '../primitives/Button/Button';
import MarkPriceChangeBottomDrawer from 'src/components/PositionsTable/MarkPriceChangeBottomDrawer';
import { useTpSlOrdersByMarket } from 'src/hooks/useTpSlOrdersByMarket';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import { PerpMarketConfig } from '@drift-labs/sdk';
import useModal from 'src/components/Modals/hooks/useModal';
import { BalanceDisplayType } from 'src/@types/types';
import { TOOLTIP_TRIGGER_DECORATION_CLASSNAME } from 'src/components/primitives/Tooltip/consts';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { signDependentValue } from 'src/utils/signDependentValue';
import OpenPositionsTableMarginDisplay from 'src/components/PositionsTable/OpenPositionsTableMarginDisplay';
import useExpandableItem, {
	ExpandableItemParams,
} from 'src/hooks/lists/expandable/useExpandableItem';
import healthDependentClassName from 'src/utils/healthDependentClassName';
import { usePositionSummary } from 'src/hooks/usePositionSummary';
import { useUnclaimablePnlWarning } from 'src/hooks/useUnclaimablePnlWarning';
import { UnclaimablePnlWarning } from './UnclaimablePnlWarning';

interface OpenPositionsMobileCardProps extends ExpandableItemParams {
	uiMarket: UIMarket;
	marketSymbol: string;
	position: AccountOpenPosition;
	positionDirection: 'long' | 'short';
	showAccountValues: boolean;
	displayValues: {
		baseSize: BigNum;
		positionPnl: BigNum;
		notionalPnl: string;
		percentagePnl: string;
	};
	handleShowPnlCard: () => void;
	handleSelection: (market: PerpMarketConfig) => void;
	sizeDisplayType: BalanceDisplayType;
	onToggleSizeDisplayType: () => void;
}

const VALUE_CLASSNAME = 'typo-t4';

export const OpenPositionsMobileCard = ({
	uiMarket,
	marketSymbol,
	position,
	positionDirection,
	showAccountValues,
	displayValues,
	handleShowPnlCard,
	areAllItemsExpanded,
	handleItemExpandedChange,
	handleSelection,
	sizeDisplayType,
	onToggleSizeDisplayType,
}: OpenPositionsMobileCardProps) => {
	const { show } = useModal();
	const positionPnlNum = displayValues.positionPnl.toNum();

	const { isExpanded, handleExpandedChange } = useExpandableItem({
		areAllItemsExpanded,
		handleItemExpandedChange,
	});

	const perpMarketsLookup = usePerpMarketsLookup();

	const { closePositionWithMarketOrder, isPending: isClosingPosition } =
		useClosePositionWithMarketOrder({
			marketIndex: uiMarket.marketIndex,
			baseSize: BigNum.from(position.baseSize.abs(), BASE_PRECISION_EXP),
		});

	const handleClosePosition = (uiOrderType: UIOrderType) => {
		if (uiOrderType === 'market') {
			closePositionWithMarketOrder();
			return;
		}

		show({
			type: 'close_position',
			props: { marketIndex: position.marketIndex, uiOrderType },
		});
	};

	const handleOpenTpSlModal = () => {
		show({
			type: 'tp_sl',
			props: { marketIndex: position.marketIndex },
		});
	};

	const notionalSize = BigNum.from(
		position.notional,
		QUOTE_PRECISION_EXP
	).toNotional();

	const baseSize = displayValues.baseSize.prettyPrint();

	const markPrice = useMarkPrice(uiMarket.marketId);
	const markPriceToUse = markPrice;

	const liqPriceBigNum = BigNum.from(position.liqPrice, PRICE_PRECISION_EXP);
	const entryPriceBigNum = BigNum.from(
		position.entryPrice,
		PRICE_PRECISION_EXP
	);
	const feesAndFundingPnl = BigNum.from(
		position.feesAndFundingPnl,
		QUOTE_PRECISION_EXP
	);

	const selection: 'cross' | `iso-${number}` = position.isIsolated
		? `iso-${position.marketIndex}`
		: 'cross';
	const { healthPercentFormatted, health } = usePositionSummary(selection);

	const tpSlOrders = useTpSlOrdersByMarket(
		position.marketIndex,
		positionDirection === 'long'
	);
	const hasAnyTpSlOrders =
		!!tpSlOrders.takeProfitPositionOrder ||
		!!tpSlOrders.stopLossPositionOrder ||
		tpSlOrders.takeProfitPartialOrders.length > 0 ||
		tpSlOrders.stopLossPartialOrders.length > 0;

	const handleClickMarket = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		handleSelection(perpMarketsLookup[position.marketIndex]);
	};

	const unclaimablePnlWarning = useUnclaimablePnlWarning(position);

	const [showMarkPriceBottomDrawer, setShowMarkPriceBottomDrawer] =
		useState(false);

	return (
		<>
			<MobileTableCard.Root
				expanded={isExpanded}
				setExpanded={handleExpandedChange}
			>
				<MobileTableCard.CollapsibleHeader expanded={isExpanded}>
					<div className="flex w-full items-center justify-between">
						<MarketCellV2
							className="w-fit py-0"
							direction={positionDirection}
							marketSymbol={marketSymbol}
							marketType={uiMarket.marketType}
							marketIndex={uiMarket.marketIndex}
							isIsolated={position.isIsolated}
							textClass="typo-t2"
							onClick={handleClickMarket}
						/>

						<div
							className="flex flex-col items-end gap-y-xs"
							data-collapsible-card-fade
						>
							<MobileTableCard.Label>P&L / ROI%</MobileTableCard.Label>
							<span
								className={cj(
									'flex items-center gap-x-xs typo-t2 whitespace-nowrap',
									signDependentValue(positionPnlNum, {
										positive: 'text-text-positive-green-button',
										negative: 'text-text-negative-red-button',
										zero: 'text-text-default',
									})
								)}
							>
								{displayValues.notionalPnl} / {displayValues.percentagePnl}
								{unclaimablePnlWarning && (
									<UnclaimablePnlWarning
										warningMessage={unclaimablePnlWarning}
									/>
								)}
							</span>
						</div>
					</div>
				</MobileTableCard.CollapsibleHeader>
				<MobileTableCard.CollapsibleContent>
					<div className="flex flex-col gap-y-lg">
						<div className="flex items-start justify-between">
							<div className="flex flex-col gap-y-xs">
								<div className="flex items-center gap-x-xs">
									<MobileTableCard.Label>
										{sizeDisplayType === 'notional' ? 'Value' : 'Size'}
									</MobileTableCard.Label>
									<IconButton
										size="sm"
										variant="tertiary"
										onClick={onToggleSizeDisplayType}
										className="text-text-tertiary"
										icon={Swap}
									/>
								</div>
								<span className="typo-t2">
									{!showAccountValues
										? '∗∗∗∗∗∗'
										: sizeDisplayType === 'notional'
											? notionalSize
											: baseSize}
								</span>
							</div>

							<div className="flex flex-col items-end gap-y-xs">
								<MobileTableCard.Label>P&L / ROI%</MobileTableCard.Label>
								<span
									className={cj(
										'flex items-center gap-x-xs typo-b2',
										signDependentValue(positionPnlNum, {
											positive: 'text-text-positive-green-button',
											negative: 'text-text-negative-red-button',
											zero: 'text-text-default',
										})
									)}
								>
									{displayValues.notionalPnl} / {displayValues.percentagePnl}
									{unclaimablePnlWarning && (
										<UnclaimablePnlWarning
											warningMessage={unclaimablePnlWarning}
										/>
									)}
								</span>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-lg">
							<div className="flex flex-col gap-y-xs">
								<MobileTableCard.Label>Health</MobileTableCard.Label>
								<span className="flex items-center gap-x-xs typo-b3">
									<Health
										size={14}
										color={healthDependentClassName(health, {
											type: 'color-var',
										})}
									/>
									<span
										onClick={() => show({ type: 'account_health', props: {} })}
										className={cj(
											TOOLTIP_TRIGGER_DECORATION_CLASSNAME,
											VALUE_CLASSNAME,
											healthDependentClassName(health, { type: 'text' })
										)}
									>
										{healthPercentFormatted}
									</span>
								</span>
							</div>
							<div className="flex flex-col items-center gap-y-xs">
								<DefinitionTooltip
									definitionId="openPositionsMargin"
									// Prevent tooltip wrapper from adding extra vertical space.
									className="flex"
								>
									<MobileTableCard.Label>Margin</MobileTableCard.Label>
								</DefinitionTooltip>
								<OpenPositionsTableMarginDisplay
									marketIndex={position.marketIndex}
									className={VALUE_CLASSNAME}
								/>
							</div>
							<div className="flex flex-col items-end gap-y-xs">
								<MobileTableCard.Label>Fees & Funding</MobileTableCard.Label>
								<NumberDisplay
									value={feesAndFundingPnl}
									type="currency"
									className="typo-b4"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-lg">
							<div className="flex flex-col gap-y-xs">
								<MobileTableCard.Label>Entry Price</MobileTableCard.Label>
								<NumberDisplay
									value={
										position.isDustPosition
											? BigNum.from(0, PRICE_PRECISION_EXP)
											: entryPriceBigNum
									}
									type="currency"
									decimals={getMarketPriceDecimals(uiMarket.baseAssetSymbol())}
									className={VALUE_CLASSNAME}
								/>
							</div>
							<div className="flex flex-col items-center gap-y-xs">
								<MobileTableCard.Label>Mark Price</MobileTableCard.Label>
								<span
									className="flex"
									onClick={() => setShowMarkPriceBottomDrawer(true)}
								>
									<NumberDisplay
										value={markPriceToUse}
										type="currency"
										decimals={getMarketPriceDecimals(
											uiMarket.baseAssetSymbol()
										)}
										className={cj(
											'text-text-default',
											VALUE_CLASSNAME,
											TOOLTIP_TRIGGER_DECORATION_CLASSNAME
										)}
									/>
								</span>
							</div>
							<div className="flex flex-col items-end gap-y-xs">
								<MobileTableCard.Label>Liq. Price</MobileTableCard.Label>
								{liqPriceBigNum.lteZero() ? (
									<span className="typo-b4">
										{PRICE_FALLBACK_DISPLAY_VALUE}
									</span>
								) : (
									<NumberDisplay
										value={liqPriceBigNum}
										type="currency"
										decimals={getMarketPriceDecimals(
											uiMarket.baseAssetSymbol()
										)}
										className={VALUE_CLASSNAME}
									/>
								)}
							</div>
						</div>

						{hasAnyTpSlOrders && (
							<div className="flex w-full items-center gap-x-sm">
								<MobileTableCard.Label>TP/SL</MobileTableCard.Label>

								<TakeProfitStopLossButton
									position={position}
									handleOpenTpSlModal={handleOpenTpSlModal}
								/>
							</div>
						)}

						<div className="mt-0 flex w-full items-center gap-x-sm">
							<Button
								size="sm"
								variant="secondary"
								className="flex-1"
								onClick={() =>
									show({
										type: 'position_max_leverage',
										props: {
											marketIndex: position.marketIndex,
										},
									})
								}
							>
								Leverage
							</Button>
							<Button
								size={'sm'}
								variant="secondary"
								className="flex-1"
								onClick={handleOpenTpSlModal}
							>
								TP/SL
							</Button>
							<Button
								size="sm"
								variant="secondary"
								className="flex-1"
								onClick={() => handleClosePosition('market')}
								disabled={isClosingPosition}
								dataTestId="close-position-market-button"
							>
								Close
							</Button>
							<IconButton
								size="sm"
								variant="secondary"
								icon={Share}
								onClick={() => handleShowPnlCard()}
							/>
						</div>
					</div>
				</MobileTableCard.CollapsibleContent>
			</MobileTableCard.Root>
			{showMarkPriceBottomDrawer && (
				<MarkPriceChangeBottomDrawer
					onClose={() => setShowMarkPriceBottomDrawer(false)}
					entryPrice={entryPriceBigNum}
					markPrice={markPriceToUse}
				/>
			)}
		</>
	);
};
